/**
 * @fileoverview The Plant Doctor's answer blocks: a problem with its fuller
 * answer, its parts, and the folded "Why we think so".
 *
 * Moved word for word out of PlantDoctor (audit batch 18, A107). Small grey
 * text is the foreground at 80% so it passes WCAG AA on the cream grounds.
 *
 * Where: PlantDoctor (the window), the answer.
 * Words (en/es): given in as props from plants.diagnose.* (the parts, evidenceHeading, reason*, moreAbout, whatToDo).
 *
 * @module AnswerBlocks
 */

import React from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Pill } from '@/shared/components/ui/Pill';
import type { CheckupAnswer, CheckupReasons } from '@/shared/api/diagnose';
import { FOLD_ROW, QUIET, SMALL_HEADING } from './doctorStyles';

/** The labels "Why we think so" is read with. */
export interface WhyLabels {
  /** "Why we think so". */
  heading: string;
  /** "You said". */
  youSaid: string;
  /** "Your photos". */
  photos: string;
  /** "The weather". */
  weather: string;
}

/** What WhyWeThinkSo is given. */
interface WhyWeThinkSoProps {
  /** The three parts; null when the check-up gave none. */
  reasons: CheckupReasons | null;
  /** The same as one paragraph: shown when the parts are missing (the check-up gave none). */
  evidence: string | null;
  /** The section's words. */
  labels: WhyLabels;
}

/**
 * "Why we think so", folded until tapped: You said · Your photos · The
 * weather, each a short labelled line; the paragraph alone when the
 * check-up gave no parts. Nothing when there is no why at all.
 *
 * @param props - WhyWeThinkSoProps.
 */
export const WhyWeThinkSo: React.FC<WhyWeThinkSoProps> = ({ reasons, evidence, labels }) => {
  const rows: Array<{ label: string; text: React.ReactNode }> = [];
  if (reasons?.youSaid) rows.push({ label: labels.youSaid, text: reasons.youSaid });
  if (reasons?.yourPhotos) rows.push({ label: labels.photos, text: reasons.yourPhotos });
  if (reasons?.weather) {
    rows.push({
      label: labels.weather,
      text: (
        <>
          {reasons.weather.facts && <span className="font-medium">{reasons.weather.facts}. </span>}
          {reasons.weather.meaning}
        </>
      ),
    });
  }
  if (rows.length === 0 && !evidence) return null;
  return (
    <details className="group rounded-md border bg-background">
      <summary className={FOLD_ROW}>
        {labels.heading}
        <ChevronDown className="h-4 w-4 shrink-0 transition-transform group-open:rotate-180" aria-hidden="true" />
      </summary>
      {rows.length > 0 ? (
        <dl className="space-y-2 px-3 pb-3 text-sm">
          {rows.map((row) => (
            <div key={row.label}>
              <dt className="font-semibold">{row.label}</dt>
              <dd>{row.text}</dd>
            </div>
          ))}
        </dl>
      ) : (
        <p className="px-3 pb-3 text-sm">{evidence}</p>
      )}
    </details>
  );
};

/** The headings of the answer's six parts, in the gardener's language. */
export interface AnswerLabels {
  /** "What it is". */
  whatItIs: string;
  /** "Why it happens". */
  why: string;
  /** "What to do now". */
  doNow: string;
  /** "Next season". */
  nextSeason: string;
  /** "Does it spread?". */
  spreads: string;
  /** "Is the fruit still good to eat?". */
  stillEdible: string;
  /** "More about it": the fold an "Also possible" problem's other parts are in. */
  more: string;
}

/** The parts shown as headed paragraphs before "What to do now", which has its own box. */
const BEFORE_DO_NOW: Array<keyof CheckupAnswer> = ['whatItIs', 'why'];
/** The parts shown after "What to do now". */
const AFTER_DO_NOW: Array<keyof CheckupAnswer> = ['nextSeason', 'spreads', 'stillEdible'];

/** What AnswerParts is given. */
interface AnswerPartsProps {
  /** The six parts. */
  answer: CheckupAnswer;
  /** Which to show, in order. */
  parts: Array<keyof CheckupAnswer>;
  /** Their headings. */
  labels: AnswerLabels;
}

/**
 * Some of the answer's parts as headed paragraphs; a part with nothing to say is left out.
 *
 * @param props - AnswerPartsProps.
 */
const AnswerParts: React.FC<AnswerPartsProps> = ({ answer, parts, labels }) => {
  const shown = parts.filter((part) => answer[part]);
  if (shown.length === 0) return null;
  return (
    <dl className="space-y-2 text-sm">
      {shown.map((part) => (
        <div key={part}>
          <dt className="font-semibold">{labels[part]}</dt>
          <dd>{answer[part]}</dd>
        </div>
      ))}
    </dl>
  );
};

/** What ProblemBlock is given: the problem's name, how it ranks, its answer, why we think so, and its labels. */
interface ProblemBlockProps {
  /** The one problem it is, by its cause. */
  name: string;
  /** Its common name, the heading; null when name is the heading. */
  commonName: string | null;
  /** The fuller answer in six parts. */
  answer: CheckupAnswer;
  /** The six parts' headings. */
  answerLabels: AnswerLabels;
  /** "Most likely" or "Also possible"; null for Grow's own suggestion, which has its own heading. */
  rank: string | null;
  /** True for the first problem: a larger title. */
  first: boolean;
  /** Why the check-up thinks so, in three parts; null when it gave none. */
  reasons: CheckupReasons | null;
  /** The same as one paragraph. */
  evidence: string | null;
  /** "Why we think so"'s words. */
  whyLabels: WhyLabels;
}

/**
 * A problem: how it ranks, its common name with its cause under it, its
 * fuller answer (all of it for the first; what to do now and, folded, the
 * rest for an "Also possible" one), and (folded) why the check-up thinks so.
 *
 * @param props - ProblemBlockProps.
 */
export const ProblemBlock: React.FC<ProblemBlockProps> = ({
  name, commonName, answer, answerLabels, rank, first, reasons, evidence, whyLabels,
}) => {
  const heading = commonName || name;
  // Under the heading: the cause when the heading is the common name.
  const under = commonName && commonName.toLowerCase() !== name.toLowerCase() ? name : null;
  const { doNow } = answer;
  const rest = [...BEFORE_DO_NOW, ...AFTER_DO_NOW].filter((part) => answer[part]);
  return (
    <section className="space-y-3">
      <div>
        {rank && (
          <Pill size="sm" tone="outline" className={cn('mb-1', first ? 'border-primary text-foreground' : QUIET)}>
            {rank}
          </Pill>
        )}
        <h3 className={cn('font-semibold font-body', first ? 'text-lg' : 'text-base')}>{heading}</h3>
        {under && <p className={cn('mt-0.5 text-sm', QUIET)}>{under}</p>}
      </div>
      {first && <AnswerParts answer={answer} parts={BEFORE_DO_NOW} labels={answerLabels} />}
      {doNow && (
        <div className="rounded-md border bg-card p-3">
          <p className={SMALL_HEADING}>{answerLabels.doNow}</p>
          <p className="mt-1 text-sm">{doNow}</p>
        </div>
      )}
      {first && <AnswerParts answer={answer} parts={AFTER_DO_NOW} labels={answerLabels} />}
      {!first && rest.length > 0 && (
        <details className="group rounded-md border bg-background">
          <summary className={FOLD_ROW}>
            {answerLabels.more}
            <ChevronDown className="h-4 w-4 shrink-0 transition-transform group-open:rotate-180" aria-hidden="true" />
          </summary>
          <div className="px-3 pb-3">
            <AnswerParts answer={answer} parts={rest} labels={answerLabels} />
          </div>
        </details>
      )}
      <WhyWeThinkSo reasons={reasons} evidence={evidence} labels={whyLabels} />
    </section>
  );
};
